import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal } from 'lucide-react';
import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';

/**
 * QuickSearch component for rapid search access from any page
 *
 * Features:
 * - Search input with icon
 * - Search button that navigates to SearchPage with query parameter
 * - Advanced Search button that navigates to SearchPage without parameters
 * - Enter key support for quick search
 * - Disabled state when query is empty
 * - Responsive design for mobile, tablet, and desktop
 *
 * Usage:
 * ```tsx
 * <QuickSearch />
 * ```
 */
export function QuickSearch() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Handles quick search - navigates to SearchPage with query parameter
   * Only navigates if query is not empty after trimming whitespace
   */
  const handleQuickSearch = () => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
    }
  };

  /**
   * Handles advanced search - navigates to SearchPage without parameters
   * Users can then use all available filters and options
   */
  const handleAdvancedSearch = () => {
    navigate('/search');
  };

  /**
   * Handles keyboard events - triggers search on Enter key
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleQuickSearch();
    }
  };

  const isSearchDisabled = !searchQuery.trim();

  return (
    <div className="space-y-3">
      {/* Search Input and Button */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4"
            aria-hidden="true"
          />
          <Input
            type="text"
            placeholder="Zoek op ECLI, trefwoord of onderwerp..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-10 h-11 bg-background"
            autoComplete="off"
            aria-label="Snel zoeken naar jurisprudentie"
          />
        </div>
        <Button
          size="default"
          className="h-11 px-6"
          onClick={handleQuickSearch}
          disabled={isSearchDisabled}
          aria-label="Zoeken"
        >
          <Search className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Zoeken</span>
        </Button>
      </div>

      {/* Advanced Search Button */}
      <Button
        variant="outline"
        size="sm"
        className="w-full sm:w-auto"
        onClick={handleAdvancedSearch}
        aria-label="Ga naar geavanceerd zoeken met alle filters"
      >
        <SlidersHorizontal className="h-4 w-4 mr-2" aria-hidden="true" />
        Geavanceerd zoeken
      </Button>
    </div>
  );
}
